import { useState, useEffect, useRef } from "react";

interface UseSqlQueryWebSocketProps {
  uri: string;
  devId: string;
}

interface QueryResult {
  columns: string[];
  rows: any[][];
  rowCount: number;
}

export const useSqlQueryWebSocket = ({
  uri,
  devId,
}: UseSqlQueryWebSocketProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const [queryResults, setQueryResults] = useState<QueryResult | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const ws = useRef<WebSocket | null>(null);
  const keepAliveInterval = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearKeepAlive = () => {
    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current);
      keepAliveInterval.current = null;
    }
  };

  const clearConnectionTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const start = () => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    setLoadingMessage("Connecting to device...");
    setError(null);
    setQueryResults(null);

    try {
      ws.current = new WebSocket(uri);

      // Connection timeout (30 seconds)
      timeoutRef.current = setTimeout(() => {
        if (!isConnected) {
          setError("Connection timeout - device did not respond");
          setLoadingMessage("");
          stop();
        }
      }, 30000);

      ws.current.onopen = () => {
        console.log("WebSocket connected");
        clearConnectionTimeout();

        // Send device ID (matching Room DB format)
        if (ws.current) {
          ws.current.send(JSON.stringify({ admin: devId }));
        }

        setIsConnected(true);
        setLoadingMessage("");

        // Start keep-alive
        keepAliveInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send("keep-alive");
          }
        }, 10000);
      };

      ws.current.onmessage = (event) => {
        console.log("Received message:", event.data);

        try {
          const data = JSON.parse(event.data);

          // Handle ready status
          if (data.status === "ready") {
            setLoadingMessage("");
            setIsConnected(true);
            return;
          }

          // Handle device response format: {success: true/false, rows: [...], error: "..."}
          if (data.success === false) {
            // Error response from device
            setError(data.error || "Query execution failed");
            setIsExecuting(false);
            return;
          }

          if (data.success === true && data.rows) {
            // Success response from device
            console.log("Query successful, processing results...");

            // Extract column names from first row (if exists)
            const columns =
              data.rows.length > 0 ? Object.keys(data.rows[0]) : [];

            // Convert rows from array of objects to array of arrays
            const rowsArray = data.rows.map((rowObj: any) =>
              columns.map((col) => rowObj[col]),
            );

            setQueryResults({
              columns: columns,
              rows: rowsArray,
              rowCount: data.rowCount || data.rows.length,
            });
            setIsExecuting(false);
            setError(null);
            return;
          }
        } catch (e) {
          // Not JSON, might be a plain text message
          if (event.data === "keep-alive") {
            // Ignore keep-alive responses
            return;
          }
          console.log("Received non-JSON message:", event.data);
        }
      };

      ws.current.onerror = (event) => {
        console.error("WebSocket error:", event);
        setError("Connection error occurred");
        setLoadingMessage("");
        setIsExecuting(false);
      };

      ws.current.onclose = () => {
        console.log("WebSocket closed");
        setIsConnected(false);
        setLoadingMessage("");
        clearKeepAlive();
        clearConnectionTimeout();
      };
    } catch (err: any) {
      console.error("Failed to create WebSocket:", err);
      setError(err.message || "Failed to connect");
      setLoadingMessage("");
    }
  };

  const stop = () => {
    clearKeepAlive();
    clearConnectionTimeout();

    if (ws.current) {
      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.send("finish");
      }
      ws.current.close();
      ws.current = null;
    }

    setIsConnected(false);
    setLoadingMessage("");
    setIsExecuting(false);
  };

  const sendQuery = (query: string) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("Not connected to device");
      return;
    }

    if (!query.trim()) {
      setError("Query cannot be empty");
      return;
    }

    setIsExecuting(true);
    setError(null);
    setQueryResults(null);

    try {
      ws.current.send(query);
      console.log("Sent query:", query);
    } catch (err: any) {
      console.error("Failed to send query:", err);
      setError(err.message || "Failed to send query");
      setIsExecuting(false);
    }
  };

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return {
    start,
    stop,
    sendQuery,
    isConnected,
    error,
    loadingMessage,
    queryResults,
    isExecuting,
  };
};
